import { useTranslations } from "@jugl-web/utils";
import React, { useMemo, useRef } from "react";
import { ReactComponent as LinkIcon } from "./assets/link.svg";
import { AutomationVariablesPopover } from "../AutomationVariablesPopover";
import { AutomationVariableDefinition } from "../../types";
import { mapVariablesToAutomationListItems } from "../../utils";
import {
  MethodSelectMenu,
  MethodSelectItem,
} from "./components/MethodSelectMenu";
import { DataInputItem } from "../DataInputRow";
import {
  VariableTokenInput,
  VariableTokenInputHandle,
} from "../../../VariableTokenInput";

type CXAutomationUrlEditorProps = {
  method: string;
  onMethodChange: (value: string) => void;
  methodItems: MethodSelectItem[];
  httpsRequest: string;
  onHttpsRequestChange: (value: string) => void;
  supportedVariables: AutomationVariableDefinition[];
  dataInputs: DataInputItem[];
};

export const CXAutomationUrlEditor: React.FC<CXAutomationUrlEditorProps> = ({
  method,
  onMethodChange,
  methodItems,
  httpsRequest,
  onHttpsRequestChange,
  supportedVariables,
  dataInputs,
}) => {
  const { t } = useTranslations();
  const inputRef = useRef<VariableTokenInputHandle>(null);

  const listItems = useMemo(
    () => mapVariablesToAutomationListItems(supportedVariables, dataInputs),
    [supportedVariables, dataInputs]
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 font-secondary text-sm font-medium text-dark">
          <LinkIcon />
          <span>
            {t({
              id: "cx-actions-page.https-url-label",
              defaultMessage: "HTTPS URL",
            })}
          </span>
        </div>
        <div className="flex items-center justify-center gap-4">
          <MethodSelectMenu
            methodItems={methodItems}
            value={method}
            onSelect={onMethodChange}
          />
          <AutomationVariablesPopover
            listItems={listItems}
            onInsert={(id) => inputRef.current?.insertAtCursor(`{${id}}`)}
          />
        </div>
      </div>
      <div className="rounded-xl border border-solid border-grey-300 px-5 py-4 transition-colors focus-within:border-[#90C9F9] focus-within:ring-2 focus-within:ring-[#90C9F9]">
        <VariableTokenInput
          ref={inputRef}
          value={httpsRequest}
          onChange={onHttpsRequestChange}
          placeholder={t({
            id: "cx-actions-page.enter-https-url-placeholder",
            defaultMessage: "Enter HTTPS URL",
          })}
          inputClassName="w-full bg-transparent border-none p-0"
        />
      </div>
    </div>
  );
};
